// walkthrough/engine.js — K843: the deterministic guide engine.
//
// This owns step state, eligibility, completion, context and recovery.
// Driver.js (or whatever replaces it) owns presentation ONLY, behind an
// adapter. The engine never asks the model anything; the model asks it.
//
// THE GUARANTEE THIS FILE EXISTS FOR:
//   a walkthrough must never continue against a different customer tenant
//   because the same button exists there.
//
// Everything below follows from that plus one other rule: completion is a
// state predicate, never a click. A click can produce a validation failure,
// a permission denial, a network error, a stale-record conflict, a
// confirmation dialog, or an accepted request still pending. None of those
// is done.

export const STATE = {
  REQUESTED: 'requested',
  GOAL_CLARIFIED: 'goal_clarified',
  ELIGIBILITY_CHECKED: 'eligibility_checked',
  GUIDE_SELECTED: 'guide_selected',
  STEP_READY: 'step_ready',
  AWAITING_USER_ACTION: 'awaiting_user_action',
  VALIDATING_OUTCOME: 'validating_outcome',
  COMPLETED: 'completed',
  // Alternatives, all distinct in telemetry. Folding abandonment into
  // success is how a feature reports 90% completion and deflects nothing.
  PAUSED: 'paused',
  BLOCKED_BY_PREREQUISITE: 'blocked_by_prerequisite',
  AWAITING_EXTERNAL_SYSTEM: 'awaiting_external_system',
  FAILED: 'failed',
  HANDED_OFF: 'handed_off',
  CANCELLED: 'cancelled',
  INVALIDATED: 'invalidated_by_context_change',
};

const TERMINAL = new Set([STATE.COMPLETED, STATE.FAILED, STATE.HANDED_OFF,
                          STATE.CANCELLED, STATE.INVALIDATED]);

/* The context a session is bound to. If any of this changes underneath the
   session, the session is no longer about what it was about. */
export function captureContext(env) {
  return {
    tenant_id: env.tenant_id ?? null,
    user_id: env.user_id ?? null,
    role: env.role ?? null,
    route_id: env.route_id ?? null,
    resource_id: env.resource_id ?? null,
    resource_version: env.resource_version ?? null,
    app_build: env.app_build ?? null,
    feature_flags: env.feature_flags ? { ...env.feature_flags } : {},
    entitlements: env.entitlements ? [...env.entitlements] : [],
  };
}

/* Which changes invalidate, and which are merely noise.
   tenant_id is first and is non-negotiable. route_id deliberately does NOT
   invalidate on its own: a guide moves the user between routes on purpose.
   It invalidates only when the user navigates somewhere the guide does not
   expect, which the caller signals with `off_guide`. */
export function diffContext(before, after) {
  const changed = [];
  const hard = ['tenant_id', 'user_id', 'role', 'app_build'];
  for (const k of hard) {
    if (before[k] !== after[k]) changed.push(k);
  }
  if (before.resource_id !== after.resource_id) changed.push('resource_id');
  if (before.resource_version !== after.resource_version) changed.push('resource_version');
  const fb = before.feature_flags || {}, fa = after.feature_flags || {};
  for (const k of new Set([...Object.keys(fb), ...Object.keys(fa)])) {
    if (fb[k] !== fa[k]) { changed.push('feature_flags.' + k); }
  }
  const eb = (before.entitlements || []).join('|');
  const ea = (after.entitlements || []).join('|');
  if (eb !== ea) changed.push('entitlements');
  return changed;
}

/* The sentence the user sees. Specific, because "something changed" is the
   kind of message people learn to ignore. */
function invalidationReason(changed) {
  if (changed.includes('tenant_id')) {
    return 'You switched organisation, so I stopped — the same buttons exist there, but this walkthrough was about the previous one.';
  }
  if (changed.includes('role') || changed.includes('entitlements')) {
    return 'Your permissions changed while we were working, so I stopped rather than guide you through something you may no longer be able to do.';
  }
  if (changed.includes('user_id')) return 'The signed-in user changed, so I stopped.';
  if (changed.includes('app_build')) {
    return 'Govern360 updated while we were working. The steps may have moved, so I stopped rather than point at the old layout.';
  }
  if (changed.includes('resource_version')) {
    return 'The record changed while we were working — someone else may have edited it. I stopped so you can look before continuing.';
  }
  if (changed.includes('resource_id')) return 'You moved to a different record, so I stopped.';
  const flag = changed.find((c) => c.startsWith('feature_flags.'));
  if (flag) return 'A setting changed that this walkthrough depends on, so I stopped.';
  return 'Something this walkthrough depends on changed, so I stopped.';
}

export class GuideSession {
  constructor({ guide, env, resolver, predicates, telemetry }) {
    this.guide = guide;
    this.ctx = captureContext(env);
    this.resolver = resolver;           // (binding) => Promise<resolution>
    this.predicates = predicates || {}; // id => () => Promise<bool|{ok,reason}>
    this.telemetry = telemetry || (() => {});
    this.state = STATE.REQUESTED;
    this.stepIndex = -1;
    this.attempts = 0;
    this.history = [];
    this.lastSay = null;
  }

  get step() { return this.stepIndex >= 0 ? this.guide.steps[this.stepIndex] : null; }
  get done() { return TERMINAL.has(this.state); }

  _to(state, detail) {
    this.history.push({ from: this.state, to: state, at: Date.now(), ...(detail || {}) });
    this.state = state;
    this.telemetry({ event: 'state', guide_id: this.guide.guide_id,
                     guide_version: this.guide.guide_version,
                     step_id: this.step ? this.step.step_id : null,
                     state, ...(detail || {}) });
    return this;
  }

  /* Called on every tick the host notices a change. Checked BEFORE anything
     else, including before completion: a save that succeeded in the previous
     tenant must not be credited to this one. */
  checkContext(env) {
    if (this.done) return { invalidated: false };
    const next = captureContext(env);
    const changed = diffContext(this.ctx, next);
    if (!changed.length) { this.ctx = next; return { invalidated: false }; }
    const say = invalidationReason(changed);
    this.ctx = next;
    this.lastSay = say;
    this._to(STATE.INVALIDATED, { changed, reason: say });
    return { invalidated: true, changed, say };
  }

  eligibility(env) {
    const e = this.guide.eligibility || {};
    const role = env.role;
    if (e.roles && e.roles.length && !e.roles.includes(role)) {
      this.lastSay = `This needs the ${e.roles.join(' or ')} role, and you're signed in as ${role || 'a role I can\'t read'}. I can explain what it does, but I shouldn't walk you into a control you can't use.`;
      this._to(STATE.BLOCKED_BY_PREREQUISITE, { reason: 'role' });
      return false;
    }
    this._to(STATE.ELIGIBILITY_CHECKED);
    return true;
  }

  select() { this._to(STATE.GUIDE_SELECTED); this.stepIndex = -1; return this; }

  async advance() {
    if (this.done) return this.state;
    this.stepIndex += 1;
    this.attempts = 0;
    if (this.stepIndex >= this.guide.steps.length) {
      this.lastSay = this.guide.completion_statement || null;
      return this._to(STATE.COMPLETED).state;
    }
    return this._prepare();
  }

  async _prepare() {
    const step = this.step;
    const res = await this.resolver(step.binding, { timeoutMs: step.timeout_ms || 10000 });
    this._lastResolution = res;
    this.telemetry({ event: 'resolve', guide_id: this.guide.guide_id,
                     step_id: step.step_id, anchor_id: step.binding.anchor_id,
                     found: res.found, visible: res.visible, enabled: res.enabled,
                     ambiguous: res.ambiguous, matched_by: res.matched_by,
                     match_count: res.match_count });

    const gate = res.permitted || { ok: res.found && res.visible && res.enabled && !res.ambiguous, say: res.say };
    if (!gate.ok) {
      /* on_missing_target is 'pause' for every required step. Skipping a
         review or confirmation step silently is the worst behaviour
         available, and some libraries offer it as a convenience. */
      this.lastSay = gate.say || 'I can\'t point at that control right now.';
      return this._to(STATE.PAUSED, { reason: 'unresolved', anchor_id: step.binding.anchor_id }).state;
    }
    this.lastSay = step.say;
    this._to(STATE.STEP_READY);
    return this._to(STATE.AWAITING_USER_ACTION).state;
  }

  /* Completion. A click is accepted ONLY for a non-consequential step. */
  async validate() {
    if (this.done) return this.state;
    const step = this.step;
    this._to(STATE.VALIDATING_OUTCOME);

    const type = (step.completion && step.completion.type) || 'click_only';
    if (step.consequential && type === 'click_only') {
      // Belt and braces: CI already refuses this guide.
      this.lastSay = 'I won\'t treat a click as proof this saved.';
      return this._to(STATE.FAILED, { reason: 'consequential_click_only' }).state;
    }

    const pid = step.completion && step.completion.predicate_id;
    const pred = pid && this.predicates[pid];
    if (!pred) {
      this.lastSay = `I can't confirm this step actually took effect — the check for it isn't wired up, so I'd rather say so than tell you it worked.`;
      return this._to(STATE.PAUSED, { reason: 'no_predicate', predicate_id: pid || null }).state;
    }

    let out;
    try { out = await pred(); }
    catch (err) { out = { ok: false, reason: (err && err.message) || 'the check threw' }; }
    const ok = out === true || (out && out.ok === true);

    if (ok) return this.advance();

    this.attempts += 1;
    const reason = (out && out.reason) || 'it did not take effect';
    /* Two strikes, then change strategy. Agents loop: in one benchmark 66% of
       wasted steps in long failed runs were retry loops. Never re-issue the
       same instruction a third time. */
    if (this.attempts >= 2) {
      this.lastSay = `That hasn't gone through twice now — ${reason}. Rather than repeat myself, let's stop here and look at what the page is reporting.`;
      return this._to(STATE.HANDED_OFF, { reason, attempts: this.attempts }).state;
    }
    this.lastSay = `That doesn't look saved yet — ${reason}.`;
    return this._to(STATE.PAUSED, { reason, attempts: this.attempts }).state;
  }

  cancel() { this.lastSay = null; return this._to(STATE.CANCELLED).state; }
}
